import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

/* eslint-disable @atlaskit/design-system/no-emotion-primitives -- legacy xcss styles require Emotion primitives; migrating them is outside this gate cleanup */
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';
import Text from '@atlaskit/primitives/text';
import Flex from '@atlaskit/primitives/components/flex';
import Box from '@atlaskit/primitives/box';
/* eslint-enable @atlaskit/design-system/no-emotion-primitives */
import { token } from '@atlaskit/tokens';
import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import Button from '@atlaskit/button/default/button';

import type { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { ReplyData } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import {
	type ProfilePictureInfo,
	ViewReplyOptions,
	getNumOfHiddenReplies,
	getProfilePicturesOfHiddenReplies,
	getDefaultHiddenReplies,
} from '../helpers/repliesHelpers.ts';

import { RepliesTogglerButton } from './RepliesTogglerButton.tsx';

const repliesTogglerStyles = {
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	height: '24px',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	left: 0,
};

const repliesTogglerBranchingStyle = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: 0,
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		top: 0,
		height: '60px',
	},
});

const repliesTogglerBranchingStyleUpdated = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: 0,
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		top: 0,
		height: '60px',
	},
});

const replyBranchUnsetHeight = css({
	'&::before': {
		height: 'unset',
	},
});

const repliesTogglerBranchingStyleWithoutReply = css({
	'&::before': {
		height: '20px',
	},
});

const unreadIndicatorStyle = xcss({
	alignItems: 'center',
	gap: 'space.075',
});

const pressableStyles = xcss({
	color: 'color.text.subtle',
	backgroundColor: 'color.background.neutral.subtle',
	paddingTop: 'space.050',
	paddingBottom: 'space.050',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	borderRadius: 'radius.small',
	userSelect: 'none',
	fontWeight: 'font.weight.medium',

	':hover': {
		cursor: 'pointer',
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});
// This snippet is being used in CommentBody.tsx
const dotStyles = css({
	height: token('space.100'),
	width: token('space.100'),
	backgroundColor: token('color.background.brand.bold'),
	borderRadius: token('radius.full'),
	margin: token('space.050'),
});

const i18n = defineMessages({
	hideRepliesText: {
		id: 'comments-common.comment-thread.hide.replies',
		defaultMessage: 'Hide replies',
		description:
			'The text is shown on a toggle button in a comment thread when there are multiple replies visible, allowing the user to collapse and hide all replies.',
	},
	hideReplyText: {
		id: 'comments-common.comment-thread.hide.reply',
		defaultMessage: 'Hide reply',
		description:
			'The text is shown on a toggle button in a comment thread when there is exactly one reply visible, allowing the user to collapse and hide that single reply.',
	},
	showMoreRepliesText: {
		id: 'comments-common.comment-thread.show.more.replies',
		defaultMessage: `Show {numOfHiddenReplies, plural, one {# more reply} other {# more replies}}`,
		description:
			'The text is shown on a toggle button in a comment thread when some replies are hidden. The placeholder {numOfHiddenReplies} is substituted with the count of hidden replies and controls the plural form (one vs. other).',
	},
	showMoreRepliesTextForStandalone: {
		id: 'comments-common.comment-thread.show.more.replies.for.standalone',
		defaultMessage: `{numOfHiddenReplies, plural, one {# more reply} other {# more replies}}`,
		description: 'Text for showing more replies for standalone comment',
	},
	showReplyText: {
		id: 'comments-common.comment-thread.show.reply',
		defaultMessage: 'Show reply',
		description:
			'The text is shown on a toggle button in a comment thread when exactly one reply is hidden, allowing the user to expand and reveal that single hidden reply.',
	},
});

const withLeftMargin = css({
	marginLeft: token('space.150'),
});

const MAX_PROFILE_PICTURES_TO_SHOW = 3;

const getFormattedShowReplyMessage = ({
	repliesLength,
	isStandaloneComment,
	numOfHiddenReplies,
	formatMessage,
}: {
	repliesLength: number;
	isStandaloneComment: boolean;
	numOfHiddenReplies: number;
	formatMessage: (messageDescriptor: any, values?: Record<string, any>) => string;
}) => {
	if (repliesLength === 1) {
		return formatMessage(i18n.showReplyText);
	}

	return formatMessage(
		isStandaloneComment ? i18n.showMoreRepliesTextForStandalone : i18n.showMoreRepliesText,
		{
			numOfHiddenReplies,
		},
	);
};

const getAnalyticsContext = ({
	analyticsSource,
	commentType,
	replies,
	hasHiddenUnreadReplies,
	currentNumOfHiddenReplies,
	shouldShowShowMoreRepliesToggle,
}: {
	analyticsSource: AnalyticsSource;
	commentType: CommentType;
	replies: ReplyData[];
	hasHiddenUnreadReplies: boolean;
	currentNumOfHiddenReplies: number;
	shouldShowShowMoreRepliesToggle: boolean;
}) => ({
	attributes: shouldShowShowMoreRepliesToggle
		? {
				name: 'repliesToggler',
				source: analyticsSource,
				commentType,
				hasHiddenUnreadReplies,
				numOfHiddenReplies: currentNumOfHiddenReplies,
				numOfReplies: replies.length,
			}
		: {
				attributes: {
					name: 'hideReplies',
					source: analyticsSource,
					commentType,
					numOfReplies: replies.length,
				},
			},
});

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

export const RepliesToggler = ({
	replies,
	unreadReplyIds,
	setReplyView,
	currentReplyView,
	commentType,
	isParentOpen,
	canAddComments,
	latestReplyIds,
	isStandaloneComment = false,
	analyticsSource,
	expandReplyEditor,
}: {
	replies: ReplyData[];
	unreadReplyIds: string[];
	setReplyView: (option: ViewReplyOptions) => void;
	currentReplyView: ViewReplyOptions;
	commentType: CommentType;
	isParentOpen: boolean;
	canAddComments: boolean;
	latestReplyIds: Set<string>;
	isStandaloneComment?: boolean;
	analyticsSource: AnalyticsSource;
	expandReplyEditor: boolean;
}): React.JSX.Element | null => {
	const focusedCommentId = useFocusedCommentIdQueryParam();
	const replyToComment = useReplyToCommentQueryParam();

	const { removeCommentQueryParams } = useInlineCommentQueryParams();

	const { formatMessage } = useIntl();

	useEffect(() => {
		// used to expand all replies of standalone comment
		const targetCommentId = focusedCommentId || replyToComment;
		if (targetCommentId) {
			// to check if the queryParam is parentCommentId or not
			const isReplyToCommentInChain = replies?.map((reply) => reply?.id)?.includes(targetCommentId);

			if (isReplyToCommentInChain) {
				setReplyView(ViewReplyOptions.ALL);
			}
		}
	}, [focusedCommentId, replyToComment, setReplyView, replies]);

	// Memoize hidden replies calculation since it's used in multiple places
	const hiddenReplies = useMemo(() => {
		// If all replies are hidden due to current view, return all replies
		if (currentReplyView === ViewReplyOptions.HIDDEN) {
			return replies;
		}
		// Otherwise, use the default hidden replies logic
		return getDefaultHiddenReplies({
			replies,
			isParentOpen,
			latestReplyIds,
		});
	}, [replies, isParentOpen, latestReplyIds, currentReplyView]);

	// for the unread indicator, we only want to render it if there are any hidden unread
	const hasHiddenUnreadReplies = useMemo(() => {
		const hiddenReplyIds = new Set(hiddenReplies.map((reply) => reply.id));
		return unreadReplyIds.some((replyId) => hiddenReplyIds.has(replyId));
	}, [unreadReplyIds, hiddenReplies]);

	const currentNumOfHiddenReplies = getNumOfHiddenReplies({
		replies,
		latestReplyIds,
		currentReplyView,
	});

	const shouldShowShowMoreRepliesToggle = currentNumOfHiddenReplies > 0;
	const shouldShowProfilePictures =
		currentReplyView !== ViewReplyOptions.ALL && shouldShowShowMoreRepliesToggle;
	const shouldShowShortenedBranchStyles = !isParentOpen && shouldShowShowMoreRepliesToggle;

	const profilePicturesHiddenReplies = useRef<ProfilePictureInfo[]>(
		shouldShowProfilePictures ? getProfilePicturesOfHiddenReplies(hiddenReplies) : [],
	);

	const showHiddenReplies = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setReplyView(ViewReplyOptions.ALL);
			(focusedCommentId || replyToComment) && removeCommentQueryParams();

			profilePicturesHiddenReplies.current = [];
		},
		[setReplyView, focusedCommentId, replyToComment, removeCommentQueryParams],
	);

	const hideReplies = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			// remove the queryParams if it exists
			(focusedCommentId || replyToComment) && removeCommentQueryParams();

			setReplyView(ViewReplyOptions.HIDDEN);

			// all replies will be considered
			profilePicturesHiddenReplies.current = getProfilePicturesOfHiddenReplies(replies);
		},
		[replies, setReplyView, focusedCommentId, replyToComment, removeCommentQueryParams],
	);

	if (expValEquals('confluence_comments_tuneup', 'isEnabled', true)) {
		if (!shouldShowShowMoreRepliesToggle) {
			return null;
		}
	}

	const formattedShowReplyMessage = getFormattedShowReplyMessage({
		repliesLength: replies.length,
		isStandaloneComment,
		numOfHiddenReplies: currentNumOfHiddenReplies,
		formatMessage,
	});

	const analyticsContext = getAnalyticsContext({
		analyticsSource,
		commentType,
		replies,
		hasHiddenUnreadReplies,
		currentNumOfHiddenReplies,
		shouldShowShowMoreRepliesToggle,
	});

	return expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
		<Box testId="replies-toggler-container">
			<RepliesTogglerButton
				hiddenReplies={hiddenReplies}
				message={formattedShowReplyMessage}
				showHiddenReplies={showHiddenReplies}
				hasHiddenUnreadReplies={hasHiddenUnreadReplies}
				analyticsContext={analyticsContext}
				isHiddenReplyView={currentReplyView === ViewReplyOptions.HIDDEN}
				expandReplyEditor={expandReplyEditor}
			/>
		</Box>
	) : (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions
		<div
			css={
				replies.length > 0 && [
					(canAddComments || currentNumOfHiddenReplies === 0) && repliesTogglerBranchingStyle,
					(canAddComments || currentNumOfHiddenReplies === 0) &&
						fg('confluence_comment_border_migration') &&
						repliesTogglerBranchingStyleUpdated,
					shouldShowShortenedBranchStyles && repliesTogglerBranchingStyleWithoutReply,
					currentReplyView === ViewReplyOptions.HIDDEN &&
						isStandaloneComment &&
						replyBranchUnsetHeight,
				]
			}
		>
			<WithCurvedBranchingStyle
				testId="replies-toggler-container"
				showDefaultStyles={replies.length > 0}
				disableLeftProp
				customStyles={repliesTogglerStyles}
			>
				<div css={withLeftMargin}>
					{shouldShowShowMoreRepliesToggle ? (
						<Pressable
							onClick={showHiddenReplies}
							testId="show-hidden-replies"
							analyticsContext={analyticsContext}
							xcss={pressableStyles}
							interactionName="show-hidden-replies"
						>
							{/* Standalone needs extra gap to account for margin differences in underlying avatar group*/}
							<Flex alignItems="center" gap={isStandaloneComment ? 'space.150' : 'space.050'}>
								{shouldShowProfilePictures && profilePicturesHiddenReplies.current.length > 0 && (
									<AvatarGroup
										data={profilePicturesHiddenReplies.current.map(
											(profilePicturesInfo: ProfilePictureInfo) => ({
												name: profilePicturesInfo.displayName,
												src: profilePicturesInfo.profilePicturePath,
											}),
										)}
										maxCount={MAX_PROFILE_PICTURES_TO_SHOW}
										appearance="stack"
										size="small"
										shouldPopupRenderToParent
										testId="replies-toggler-profile-pictures"
									/>
								)}
								<Flex xcss={unreadIndicatorStyle}>
									{formattedShowReplyMessage}
									{hasHiddenUnreadReplies && (
										<span css={dotStyles} data-testid="unread-comment-replies-indicator" />
									)}
								</Flex>
							</Flex>
						</Pressable>
					) : (
						<Button
							onClick={hideReplies}
							testId="hide-replies"
							appearance="subtle"
							analyticsContext={analyticsContext}
							interactionName="hide-replies"
						>
							<Flex alignItems="center" gap="space.050">
								<Text color="color.text" size="medium" weight="medium">
									{replies.length === 1
										? formatMessage(i18n.hideReplyText)
										: formatMessage(i18n.hideRepliesText)}
								</Text>
							</Flex>
						</Button>
					)}
				</div>
			</WithCurvedBranchingStyle>
		</div>
	);
};
